import type React from "react";
import { type ReactNode, useEffect } from "react";

import classNames from "classnames";

import { AccessibleButton } from "@/features/accessibility/components/AccessibleButton";
import { Popup, type PopupAnchor } from "./Popup";

import "./popup-menu.scss";

import type { ArrowTip } from "../../PopupMenuProvider";

const POPUP_MENU_CLOSE_EVENT = "popup-menu-close";

export const closePopupMenus = () => {
  window.dispatchEvent(new Event(POPUP_MENU_CLOSE_EVENT));
};

type PopupMenuItemProps = {
  text: string;
  icon?: ReactNode;
  description?: string | ReactNode;
  onClick: () => void;
};

const PopupMenuItem = (props: PopupMenuItemProps) => (
  <AccessibleButton
    onClick={() => {
      props.onClick();
      closePopupMenus();
    }}
  >
    <li className="popup-menu__item">
      <span className="popup-menu__item__content">
        <span className="popup-menu__item__content__icon">{props.icon}</span>

        {props.text}
      </span>
      <span className="popup-menu__item__description">{props.description}</span>
    </li>
  </AccessibleButton>
);

const PopupMenuDivider = () => <div className="popup-menu__divider" />;

const PopupMenuGroup = (props: { children: ReactNode }) => (
  <div className="popup-menu__group">{props.children}</div>
);

const PopupMenuGroupTitle = (props: { title: string }) => (
  <div className="popup-menu__group-title">{props.title}</div>
);

type PopupMenuProps = {
  anchor: PopupAnchor;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  layoutOptions?: {
    arrowTip?: ArrowTip;
  };
};

const PopupMenuComponent = (props: PopupMenuProps) => {
  useEffect(() => {
    window.addEventListener(POPUP_MENU_CLOSE_EVENT, props.onClose);
    return () => {
      window.removeEventListener(POPUP_MENU_CLOSE_EVENT, props.onClose);
    };
  }, [props.onClose]);

  return (
    <Popup
      open={props.open}
      onClose={props.onClose}
      anchor={props.anchor}
      className={classNames(
        "popup-menu",
        {
          "popup-menu--arrow-top-left":
            props.layoutOptions?.arrowTip === "top-left",
        },
        props.className,
      )}
    >
      <ul>{props.children}</ul>
    </Popup>
  );
};

interface PopupMenuType extends React.FC<PopupMenuProps> {
  Item: typeof PopupMenuItem;
  Divider: typeof PopupMenuDivider;
  GroupTitle: typeof PopupMenuGroupTitle;
  Group: typeof PopupMenuGroup;
}
export const PopupMenu = PopupMenuComponent as PopupMenuType;
PopupMenu.Item = PopupMenuItem;
PopupMenu.Divider = PopupMenuDivider;
PopupMenu.GroupTitle = PopupMenuGroupTitle;
PopupMenu.Group = PopupMenuGroup;
